import { useState } from "react";
import { MailMinus, UserCheck } from "lucide-react";
import { Button, Dialog } from "@dfws/ui";
import type { MessageBody, ThreadMessage } from "@dfws/contracts";
import { api, ApiFailure } from "../../lib/api";
import { queryClient, useMailboxes } from "../../lib/queries";
import s from "./ThreadView.module.css";

/** Bars under a message header: one-click unsubscribe for list mail, and the screener's allow button. */
export function SenderBars({ message: m, body }: { message: ThreadMessage; body: MessageBody }) {
  const mailboxes = useMailboxes();
  const inScreener = mailboxes.data?.items.find((b) => b.id === m.mailboxId)?.name === "Screener";
  return (
    <>
      {inScreener && <ScreenerBar message={m} />}
      {body.unsubscribe && <UnsubscribeBar message={m} info={body.unsubscribe} />}
    </>
  );
}

function ScreenerBar({ message: m }: { message: ThreadMessage }) {
  const [state, setState] = useState<"idle" | "busy" | "done" | string>("idle");
  const allow = async () => {
    setState("busy");
    try {
      await api("/screener/allow", { method: "POST", body: { messageId: m.id } });
      setState("done");
      void queryClient.invalidateQueries({ queryKey: ["threads"] });
      void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    } catch (err) {
      setState(err instanceof ApiFailure ? err.message : "That did not work. Try again.");
    }
  };
  return (
    <div className={s.imagesBar} role="status">
      <UserCheck aria-hidden />
      {state === "done" ? (
        <span>{m.from.email} is allowed. Their mail goes to your inbox from now on.</span>
      ) : (
        <>
          <span>First message from {m.from.email}. The screener is holding it until you decide.</span>
          {state !== "idle" && state !== "busy" && <span className={s.error}>{state}</span>}
          <button type="button" onClick={() => void allow()} disabled={state === "busy"}>
            Allow sender
          </button>
        </>
      )}
    </div>
  );
}

function UnsubscribeBar({ message: m, info }: { message: ThreadMessage; info: NonNullable<MessageBody["unsubscribe"]> }) {
  const [confirm, setConfirm] = useState(false);
  const [state, setState] = useState<"idle" | "busy" | "done" | string>("idle");
  const sender = m.from.name || m.from.email;
  const oneClick = async () => {
    setState("busy");
    try {
      await api(`/messages/${m.id}/unsubscribe`, { method: "POST" });
      setState("done");
    } catch (err) {
      setState(err instanceof ApiFailure ? err.message : "The unsubscribe request failed.");
    }
    setConfirm(false);
  };
  return (
    <div className={s.imagesBar}>
      <MailMinus aria-hidden />
      {state === "done" ? (
        <span role="status">Unsubscribed from {sender}. Some senders take a few days to stop.</span>
      ) : (
        <>
          <span>Mailing list mail from {sender}.</span>
          {state !== "idle" && state !== "busy" && <span className={s.error} role="alert">{state}</span>}
          {info.oneClick ? (
            <button type="button" onClick={() => setConfirm(true)} disabled={state === "busy"}>Unsubscribe</button>
          ) : info.url ? (
            <a href={info.url} target="_blank" rel="noopener noreferrer nofollow" className={s.barLink}>Unsubscribe</a>
          ) : info.mailto ? (
            <a href={info.mailto} className={s.barLink}>Unsubscribe by email</a>
          ) : null}
        </>
      )}
      <Dialog open={confirm} onOpenChange={setConfirm} title={`Unsubscribe from ${sender}?`} description="Workspace asks the sender to stop, without opening their website. Messages you already have stay where they are.">
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
          <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="primary" loading={state === "busy"} onClick={() => void oneClick()}>Unsubscribe</Button>
        </div>
      </Dialog>
    </div>
  );
}
